import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  Button,
  Input,
  Textarea,
  Spinner,
  Text,
  tokens,
  Title2,
  Subtitle2,
  Combobox,
  Option,
  OptionGroup,
  Dropdown,
  Tab,
  TabList,
  MessageBar,
  MessageBarBody,
  Tooltip,
  Field,
} from '@fluentui/react-components';
import {
  Add20Regular,
  Dismiss20Regular,
  Info16Regular,
} from '@fluentui/react-icons';
import { apiConfig } from '../authConfig';
import { useAuth } from '../hooks/useAuth';

const GLOBAL_SETTING_KEYS = ['QPSList', 'DPSList', 'PerfFlag', 'EnableBorrowMachine'];
const AIRFLOW_REQUIRED_SETTING_KEYS = ['Namespace', 'DagId', 'Subcluster', 'AirflowJobName'];
const MAX_EXP_TESTS = 3;
const CLONED_BASE_TEST_NAME = 'ClonedBase';
const NO_SPECIFIED_MACHINE_OPTION = '__NO_SPECIFIED_MACHINE__';
const NO_SPECIFIED_MACHINE_LABEL = '-- Select a machine --';

const PERFFLAG_OPTIONS = [
  'Default', 'Logman', 'F1', 'XPerf', 'F1Lock', 'Customer',
  'APCounter', 'F1_Logman', 'PyCounter', 'CockpitMetrics',
  'JarvisMetrics', 'AMDuProf', 'Prometheus',
];

const AIRFLOW_PERFFLAG_OPTIONS = [
  'default',
  'dotnetTraceLinux',
  'perfviewWindowsDotnet',
  'perfviewWindows',
  'perfLinux',
  'jfrLinux',
  'asyncProfilerLinux',
  'pyspyLinux',
];

const GLOBAL_SETTING_OPTIONS = {
  EnableBorrowMachine: ['Yes', 'No'],
};

const isGlobalSettingKey = (key) => GLOBAL_SETTING_KEYS.includes(key);

const stripGlobalSettings = (settings = {}) => Object.fromEntries(
  Object.entries(settings).filter(([key]) => !isGlobalSettingKey(key))
);

const pickGlobalSettings = (settings = {}) => Object.fromEntries(
  Object.entries(settings).filter(([key]) => isGlobalSettingKey(key))
);

const getClonableJobTests = (job) => {
  const jobTests = Array.isArray(job?.Tests) ? job.Tests : [];
  if (!job?.DualBase) return jobTests;

  return jobTests.filter((test) => test.Name !== CLONED_BASE_TEST_NAME);
};

const renderSettingLabel = (setting, helpClassName) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <span>{setting.key}</span>
    {setting.Help && (
      <Tooltip content={setting.Help} relationship="description">
        <Info16Regular className={helpClassName} />
      </Tooltip>
    )}
  </div>
);

const parseMachineTypeOptions = (machineType) => {
  if (!machineType) return [];
  return Array.from(new Set(machineType
    .split('|')
    .map((value) => value.trim())
    .filter(Boolean)));
};

const parseDelimitedSettingOptions = (rawValue) => {
  if (typeof rawValue !== 'string' || !rawValue.includes('||||')) return [];
  const options = rawValue
    .split('||||')
    .map((value) => value.trim())
    .filter(Boolean);
  return options.length > 1 ? options : [];
};

const getInitialSettingValue = (rawValue) => {
  const options = parseDelimitedSettingOptions(rawValue);
  if (options.length > 0) {
    return options[0];
  }
  return rawValue ?? '';
};

const normalizeSettingValue = (value) => (
  typeof value === 'string' ? value.trim() : value
);

const normalizeTestNames = (tests) => {
  let expIndex = 1;
  return tests.map((test) => {
    if (test.isBase) {
      return { ...test, name: 'Base' };
    }
    const normalizedName = `Exp${expIndex}`;
    expIndex += 1;
    return { ...test, name: normalizedName };
  });
};

const AutoResizingSettingTextarea = ({ className, value, onChange }) => {
  const textareaRef = useRef(null);

  const resizeToContent = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, []);

  useLayoutEffect(() => {
    resizeToContent();
  }, [value, resizeToContent]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return undefined;

    let previousWidth = textarea.clientWidth;
    const handleWidthChange = () => {
      const currentWidth = textarea.clientWidth;
      if (currentWidth !== previousWidth) {
        previousWidth = currentWidth;
        resizeToContent();
      }
    };

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', handleWidthChange);
      return () => window.removeEventListener('resize', handleWidthChange);
    }

    const resizeObserver = new ResizeObserver(handleWidthChange);
    resizeObserver.observe(textarea.parentElement || textarea);
    return () => resizeObserver.disconnect();
  }, [resizeToContent]);

  return (
    <Textarea
      ref={textareaRef}
      className={className}
      value={value ?? ''}
      onChange={onChange}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
          event.preventDefault();
        }
      }}
      resize="none"
      wrap="soft"
    />
  );
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '1440px',
    marginLeft: 'auto',
    marginRight: 'auto',
    ...shorthands.padding(tokens.spacingHorizontalM, tokens.spacingHorizontalL),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.gap(tokens.spacingVerticalL),
  },
  pageHeader: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXS),
  },
  pageLead: {
    color: tokens.colorNeutralForeground3,
    maxWidth: '960px',
  },
  cloneNotice: {
    color: tokens.colorBrandForeground1,
    backgroundColor: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    ...shorthands.gap(tokens.spacingHorizontalL),
    alignItems: 'stretch',
  },
  sectionCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  sectionBody: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  sectionTitle: {
    color: tokens.colorNeutralForeground2,
  },
  sectionContent: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  jobDetailsLayout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.65fr) minmax(340px, 1fr)',
    alignItems: 'start',
    ...shorthands.gap(tokens.spacingHorizontalL, tokens.spacingVerticalL),
    '@media (max-width: 980px)': {
      gridTemplateColumns: '1fr',
    },
  },
  jobDetailsLayoutSingle: {
    gridTemplateColumns: '1fr',
  },
  primaryPanel: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  primaryFieldsGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 400px) minmax(0, 1fr)',
    alignItems: 'start',
    ...shorthands.gap(tokens.spacingHorizontalM, tokens.spacingVerticalM),
    '@media (max-width: 720px)': {
      gridTemplateColumns: '1fr',
    },
  },
  executionPanel: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  panelTitle: {
    color: tokens.colorNeutralForeground2,
  },
  perTestTitle: {
    color: tokens.colorNeutralForeground2,
    marginBottom: tokens.spacingVerticalS,
  },
  panelHint: {
    color: tokens.colorNeutralForeground3,
  },
  executionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    alignItems: 'start',
    ...shorthands.gap(tokens.spacingHorizontalM, tokens.spacingVerticalM),
    '@media (max-width: 640px)': {
      gridTemplateColumns: '1fr',
    },
  },
  fieldsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    ...shorthands.gap(tokens.spacingHorizontalM, tokens.spacingVerticalM),
  },
  fieldSpanFull: {
    gridColumn: '1 / -1',
  },
  settingsPanel: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  perTestPanel: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
  },
  formInput: {
    width: '100%',
    minWidth: 0,
  },
  settingTextarea: {
    '& textarea': {
      maxHeight: 'none',
      overflowY: 'hidden',
      overflowWrap: 'anywhere',
      whiteSpace: 'pre-wrap',
    },
  },
  testSettingsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    alignItems: 'start',
    ...shorthands.gap(tokens.spacingHorizontalM, tokens.spacingVerticalM),
    '@media (max-width: 900px)': {
      gridTemplateColumns: '1fr',
    },
  },
  helpIcon: {
    color: tokens.colorNeutralForeground3,
    marginLeft: '4px',
    cursor: 'pointer',
    verticalAlign: 'middle',
  },
  tabContent: {
    marginTop: tokens.spacingVerticalS,
  },
  tabBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  settingsHeader: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXXS),
  },
  settingsMachineRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    ...shorthands.gap(tokens.spacingHorizontalM),
    alignItems: 'start',
    '@media (max-width: 900px)': {
      gridTemplateColumns: '1fr',
    },
  },
  submitRow: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    ...shorthands.gap(tokens.spacingHorizontalS),
    marginTop: tokens.spacingVerticalL,
    alignItems: 'center',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    ...shorthands.padding('20px'),
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  pageTitle: {
    marginBottom: '0',
  },
  hintText: {
    ...shorthands.padding('12px'),
    display: 'block',
    color: tokens.colorNeutralForeground4,
  },
  machineHint: {
    color: tokens.colorNeutralForeground4,
    fontSize: '11px',
  },
  messageBar: {
    marginBottom: '8px',
  },
  fullWidthDropdownField: {
    '& button': {
      width: '100%',
    },
  },
  typeFieldInput: {
    maxWidth: '400px',
    '@media (max-width: 720px)': {
      maxWidth: '100%',
    },
  },
});

function PolarisSubmitJobPage() {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessToken, accounts } = useAuth();
  const cloneJob = location.state?.cloneJob;
  const cloneSourceTests = getClonableJobTests(cloneJob);
  const [hasUserChangedType, setHasUserChangedType] = useState(false);

  const userAlias = accounts[0]?.username?.split('@')[0] || '';

  const [testTypes, setTestTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [machinesByType, setMachinesByType] = useState([]);
  const [loadingMachines, setLoadingMachines] = useState(false);
  const [defaultValues, setDefaultValues] = useState({});

  const [selectedType, setSelectedType] = useState(cloneJob?.Type || '');
  const [typeQuery, setTypeQuery] = useState(cloneJob?.Type || '');
  const [priority, setPriority] = useState(cloneJob ? String(cloneJob.Priority ?? 2) : '2');
  const [jobName, setJobName] = useState(cloneJob?.Name || '');
  const [machineCount, setMachineCount] = useState(cloneJob ? String(cloneJob.MachineCount ?? 1) : '1');
  const [note, setNote] = useState(cloneJob?.Note || '');
  // Job details contain the effective QueueTimeout, which may come from the
  // TestType default even when the original submission omitted the field.
  // Keep the clone override empty unless the user explicitly enters one.
  const [queueTimeout, setQueueTimeout] = useState('');
  const [runTimeout, setRunTimeout] = useState(cloneJob?.RunTimeout != null ? String(cloneJob.RunTimeout) : '');
  const [specifiedMachine, setSpecifiedMachine] = useState(cloneJob?.SpecifiedMachine || '');
  const [selectedMachineType, setSelectedMachineType] = useState(cloneJob?.MachineType || '');
  const [globalSettingValues, setGlobalSettingValues] = useState(() =>
    cloneSourceTests.length ? pickGlobalSettings(cloneSourceTests[0]?.Settings) : {}
  );

  const [tests, setTests] = useState(() => {
    if (cloneSourceTests.length) {
      return normalizeTestNames(cloneSourceTests.map((t) => ({
        name: t.Name,
        isBase: t.IsBase,
        settings: stripGlobalSettings(t.Settings),
      })));
    }
    return [{ name: 'Base', isBase: true, settings: {} }];
  });
  const [activeTab, setActiveTab] = useState(() => {
    if (!cloneSourceTests.length) return 'Base';
    return cloneSourceTests[0].IsBase ? 'Base' : 'Exp1';
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessToken();
        const resp = await fetch(`${apiConfig.apiBaseUrl}/polaris/test-types`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) throw new Error(`API error: ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) {
          setTestTypes(data);
          setLoadingTypes(false);
        }
      } catch (err) {
        if (!cancelled) setLoadingTypes(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getAccessToken]);

  const selectedTestType = useMemo(
    () => testTypes.find((t) => t.Id === selectedType),
    [testTypes, selectedType]
  );

  const machineTypeOptions = useMemo(
    () => parseMachineTypeOptions(selectedTestType?.MachineType),
    [selectedTestType?.MachineType]
  );

  const hasConfiguredMachineType = Boolean(String(selectedTestType?.MachineType || '').trim());
  const effectiveMachineType = selectedMachineType || (machineTypeOptions.length === 1 ? machineTypeOptions[0] : '');
  const requiresMachineType = hasConfiguredMachineType && selectedTestType?.MachineType !== 'Airflow';

  const visibleMachineGroups = useMemo(() => {
    if (!effectiveMachineType) {
      return machinesByType;
    }
    return machinesByType.filter(({ machineType }) => machineType === effectiveMachineType);
  }, [machinesByType, effectiveMachineType]);

  const defaultSettings = useMemo(() => {
    if (!selectedTestType?.ClientSettingConfig) return [];
    try {
      const parsed = JSON.parse(selectedTestType.ClientSettingConfig);
      return Object.entries(parsed)
        .map(([key, config]) => ({ key, ...config }))
        .filter((s) => !s.Hidden)
        .sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0));
    } catch {
      return [];
    }
  }, [selectedTestType]);

  const hiddenSettings = useMemo(() => {
    if (!selectedTestType?.ClientSettingConfig) return [];
    try {
      const parsed = JSON.parse(selectedTestType.ClientSettingConfig);
      return Object.entries(parsed)
        .map(([key, config]) => ({ key, ...config }))
        .filter((s) => s.Hidden);
    } catch {
      return [];
    }
  }, [selectedTestType]);

  const globalSettings = useMemo(
    () => defaultSettings.filter((setting) => isGlobalSettingKey(setting.key)),
    [defaultSettings]
  );

  const hasGlobalSettingsContent = requiresMachineType || globalSettings.length > 0;

  const perTestSettings = useMemo(
    () => defaultSettings.filter((setting) => !isGlobalSettingKey(setting.key)),
    [defaultSettings]
  );

  const cloneFieldsRef = useRef(!!cloneJob);

  useEffect(() => {
    if (!selectedTestType) return;
    if (cloneFieldsRef.current) {
      cloneFieldsRef.current = false;
      return;
    }
    if (selectedTestType.MachineCount) {
      setMachineCount(String(selectedTestType.MachineCount));
    }
    setQueueTimeout('');
    setRunTimeout('');
  }, [selectedTestType]);

  useEffect(() => {
    // TestTypes are loaded asynchronously. Do not erase the cloned machine
    // type during the initial render while its TestType is still unavailable.
    if (!selectedTestType) return;

    if (!selectedTestType.MachineType) {
      setSelectedMachineType('');
      return;
    }

    const options = parseMachineTypeOptions(selectedTestType.MachineType);
    if (!options.length) {
      setSelectedMachineType(selectedTestType.MachineType);
      return;
    }

    setSelectedMachineType((currentValue) => {
      if (currentValue && options.includes(currentValue)) {
        return currentValue;
      }
      return '';
    });
  }, [selectedTestType?.MachineType]);

  useEffect(() => {
    if (!selectedType) return;
    const keepClonedSettings = Boolean(
      cloneJob && !hasUserChangedType && selectedType === cloneJob.Type
    );
    if (keepClonedSettings) {
      return;
    }
    const initialSettings = {};
    for (const s of perTestSettings) {
      initialSettings[s.key] = getInitialSettingValue(defaultValues[s.key]);
    }
    const initialGlobalSettings = {};
    for (const s of globalSettings) {
      initialGlobalSettings[s.key] = getInitialSettingValue(defaultValues[s.key]);
    }
    setTests([{ name: 'Base', isBase: true, settings: initialSettings }]);
    setGlobalSettingValues(initialGlobalSettings);
    setActiveTab('Base');
  }, [selectedType, perTestSettings, globalSettings, defaultValues, cloneJob, hasUserChangedType]);

  useEffect(() => {
    if (!selectedType || !machineTypeOptions.length) {
      setMachinesByType([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingMachines(true);
        const token = await getAccessToken();
        const resp = await fetch(
          `${apiConfig.apiBaseUrl}/polaris/test-types/${encodeURIComponent(selectedType)}/machines`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!resp.ok) throw new Error(`API error: ${resp.status}`);

        const data = await resp.json();
        const machines = Array.isArray(data) ? data : [];
        const machinesByRawType = new Map();
        for (const machine of machines) {
          const rawMachineType = machine.RawMachineType;
          if (!machinesByRawType.has(rawMachineType)) {
            machinesByRawType.set(rawMachineType, []);
          }
          machinesByRawType.get(rawMachineType).push(machine);
        }

        const responses = machineTypeOptions.map((machineType) => ({
          machineType,
          machines: machinesByRawType.get(machineType) || [],
        }));
        if (!cancelled) setMachinesByType(responses);
      } catch {
        if (!cancelled) setMachinesByType([]);
      } finally {
        if (!cancelled) setLoadingMachines(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedType, machineTypeOptions, getAccessToken]);

  useEffect(() => {
    if (!selectedType) {
      setDefaultValues({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessToken();
        const resp = await fetch(
          `${apiConfig.apiBaseUrl}/polaris/test-types/${encodeURIComponent(selectedType)}/default-settings`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!resp.ok) throw new Error(`API error: ${resp.status}`);
        const data = await resp.json();
        const settingsMap = {};
        if (Array.isArray(data)) {
          for (const row of data) {
            if (row.Key) settingsMap[row.Key] = row.Value ?? '';
          }
        }
        if (!cancelled) setDefaultValues(settingsMap);
      } catch {
        if (!cancelled) setDefaultValues({});
      }
    })();
    return () => { cancelled = true; };
  }, [selectedType, getAccessToken]);

  const handleTypeChange = (_e, data) => {
    const val = data.optionValue || '';
    if (cloneJob && val !== cloneJob.Type) {
      setHasUserChangedType(true);
    }
    setSelectedType(val);
    setTypeQuery(val);
    setSpecifiedMachine('');
  };

  const filteredTypeOptions = useMemo(() => {
    const all = testTypes.filter((t) => t.IsEnabled).map((t) => t.Id).sort();
    if (!typeQuery || typeQuery === selectedType) return all;
    const lower = typeQuery.toLowerCase();
    return all.filter((id) => id.toLowerCase().includes(lower));
  }, [testTypes, typeQuery, selectedType]);

  const updateTestSetting = (testName, key, value) => {
    setTests((prev) =>
      prev.map((t) =>
        t.name === testName ? { ...t, settings: { ...t.settings, [key]: value } } : t
      )
    );
  };

  const updateGlobalSetting = (key, value) => {
    setGlobalSettingValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSpecifiedMachineChange = (optionValue) => {
    if (optionValue === NO_SPECIFIED_MACHINE_OPTION || !optionValue) {
      setSpecifiedMachine('');
      return;
    }

    setSpecifiedMachine(optionValue);
    if (!selectedMachineType) {
      const firstMatchingGroup = machinesByType.find(({ machines }) => (
        machines.some((machine) => machine.machineName === optionValue)
      ));
      if (firstMatchingGroup) {
        setSelectedMachineType(firstMatchingGroup.machineType);
      }
    }
  };

  const renderSettingInput = (setting, value, onChange, defaultValue) => {
    const options = setting.key === 'PerfFlag'
      ? (selectedTestType?.MachineType === 'Airflow'
        ? AIRFLOW_PERFFLAG_OPTIONS
        : PERFFLAG_OPTIONS)
      : (GLOBAL_SETTING_OPTIONS[setting.key] || parseDelimitedSettingOptions(defaultValue));
    if (options.length > 0) {
      const resolvedValue = options.includes(value) ? value : '';
      return (
        <Dropdown
          className={mergeClasses(classes.formInput, classes.fullWidthDropdownField)}
          value={resolvedValue}
          selectedOptions={resolvedValue ? [resolvedValue] : []}
          onOptionSelect={(_e, data) => onChange(data.optionValue || '')}
        >
          {options.map((option) => (
            <Option key={option} value={option}>{option}</Option>
          ))}
        </Dropdown>
      );
    }

    return (
      <AutoResizingSettingTextarea
        className={mergeClasses(classes.formInput, classes.settingTextarea)}
        value={value ?? ''}
        onChange={(_e, data) => onChange(data.value)}
      />
    );
  };

  const addTest = () => {
    if (tests.filter((test) => !test.isBase).length >= MAX_EXP_TESTS) {
      return;
    }

    const previousTest = tests[tests.length - 1];
    const inheritedSettings = { ...(previousTest?.settings || {}) };
    const nextTests = normalizeTestNames([
      ...tests,
      { name: '', isBase: false, settings: inheritedSettings },
    ]);
    const nextActiveTest = nextTests[nextTests.length - 1];

    setTests(nextTests);
    if (nextActiveTest) {
      setActiveTab(nextActiveTest.name);
    }
  };

  const removeTest = (testName) => {
    const removingIndex = tests.findIndex((test) => test.name === testName);
    const remainingTests = normalizeTestNames(tests.filter((test) => test.name !== testName));
    const nextActiveTest = remainingTests[removingIndex] || remainingTests[removingIndex - 1] || remainingTests[0];

    setTests(remainingTests);
    if (nextActiveTest) {
      setActiveTab(nextActiveTest.name);
    }
  };

  const handleSubmit = async () => {
    if (!selectedType || !jobName.trim()) {
      setSubmitError('Type and Name are required.');
      return;
    }

    if (requiresMachineType && !effectiveMachineType) {
      setSubmitError('MachineType is required.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    const body = {
      Name: jobName.trim(),
      Type: selectedType,
      Priority: parseInt(priority, 10),
      User: userAlias,
      Note: note,
      MachineCount: parseInt(machineCount, 10),
      Origin: 'Portal - LegoWeb',
      Tests: tests.map((t) => {
        const settings = { ...t.settings, ...globalSettingValues };
        const airflowRequiredSettings = {};

        if (selectedTestType?.MachineType === 'Airflow') {
          for (const key of AIRFLOW_REQUIRED_SETTING_KEYS) {
            airflowRequiredSettings[key] = normalizeSettingValue(
              settings[key] ?? defaultValues[key] ?? ''
            );
          }
        }

        for (const s of hiddenSettings) {
          delete settings[s.key];
        }

        // Trim string values and omit empty visible settings. This also prevents
        // pasted whitespace from breaking downstream parsing. Keys are left as-is.
        for (const key of Object.keys(settings)) {
          const normalizedValue = normalizeSettingValue(settings[key]);
          if (normalizedValue == null || normalizedValue === '') {
            delete settings[key];
          } else {
            settings[key] = normalizedValue;
          }
        }

        if (selectedTestType?.MachineType === 'Airflow') {
          Object.assign(settings, airflowRequiredSettings);
        }
        return { Name: t.name, IsBase: t.isBase, Settings: settings };
      }),
    };
    if (effectiveMachineType) body.MachineType = effectiveMachineType;
    if (queueTimeout) body.QueueTimeout = parseInt(queueTimeout, 10);
    if (runTimeout) body.RunTimeout = parseInt(runTimeout, 10);
    if (specifiedMachine) body.SpecifiedMachine = specifiedMachine;

    try {
      const token = await getAccessToken();
      const resp = await fetch(`${apiConfig.apiBaseUrl}/polaris/jobs`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.message || `API error: ${resp.status}`);
      }
      const data = await resp.json();
      setSubmitSuccess(data.message || `Job submitted successfully!`);
      if (data.jobId) setTimeout(() => navigate(`/polaris/job/${data.jobId}`), 1500);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const activeTest = tests.find((t) => t.name === activeTab);
  const hasExecutionParameters = selectedTestType?.MachineType !== 'Airflow';

  if (loadingTypes) {
    return (
      <div className={classes.container}>
        <div className={classes.loading}>
          <Spinner label="Loading test types..." />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.pageHeader}>
        <Title2 className={classes.pageTitle}>{cloneJob ? 'Clone Job' : 'Submit Job'}</Title2>
        <Text className={classes.pageLead}>
          {cloneJob
            ? 'Start from a copied job, then adjust its identity, execution parameters, and test settings before submitting.'
            : 'Fill in the primary job information first, then tune execution parameters and test settings.'}
        </Text>
        {cloneJob && (
          <Text className={classes.cloneNotice}>
            Cloning from job {cloneJob.Id || cloneJob.Name || selectedType}. Review the copied values before submitting.
          </Text>
        )}
      </div>

      <section className={classes.section}>
        <Subtitle2 className={classes.sectionTitle}>Job details</Subtitle2>
        <div className={classes.sectionContent}>
          <div className={mergeClasses(
            classes.jobDetailsLayout,
            !hasExecutionParameters && classes.jobDetailsLayoutSingle
          )}>
            <div className={classes.primaryPanel}>
              <Subtitle2 className={classes.panelTitle}>Primary information</Subtitle2>
              <div className={classes.primaryFieldsGrid}>
                <Field label="Type" required>
                  <Combobox
                    className={mergeClasses(classes.formInput, classes.typeFieldInput)}
                    placeholder="Search or select a test type"
                    value={typeQuery || selectedType}
                    onChange={(_e) => setTypeQuery(_e.target.value)}
                    onOptionSelect={handleTypeChange}
                    freeform
                  >
                    {filteredTypeOptions.map((id) => (
                      <Option key={id} value={id}>{id}</Option>
                    ))}
                  </Combobox>
                </Field>

                <Field label="Name" required>
                  <Input
                    className={classes.formInput}
                    placeholder="Required, please name your job"
                    value={jobName}
                    onChange={(_e, d) => setJobName(d.value)}
                  />
                </Field>

                <Field className={classes.fieldSpanFull} label="Note">
                  <Input
                    className={classes.formInput}
                    placeholder="Optional, add tags or notes to this job"
                    value={note}
                    onChange={(_e, d) => setNote(d.value)}
                  />
                </Field>
              </div>
            </div>

            {hasExecutionParameters && (
              <div className={classes.executionPanel}>
                <Subtitle2 className={classes.panelTitle}>Execution parameters</Subtitle2>
                <div className={classes.executionGrid}>
                  <Field label="Priority" required>
                    <Dropdown
                      className={mergeClasses(classes.formInput, classes.fullWidthDropdownField)}
                      value={priority}
                      selectedOptions={[priority]}
                      onOptionSelect={(_e, d) => setPriority(d.optionValue)}
                    >
                      <Option value="1">1</Option>
                      <Option value="2">2</Option>
                      <Option value="3">3</Option>
                      <Option value="4">4</Option>
                    </Dropdown>
                  </Field>

                  <Field label="MachineCount">
                    <Input
                      className={classes.formInput}
                      value={machineCount}
                      type="number"
                      disabled
                    />
                  </Field>

                  <Field label="QueueTimeout">
                    <Input
                      className={classes.formInput}
                      placeholder="Optional"
                      value={queueTimeout}
                      onChange={(_e, d) => setQueueTimeout(d.value)}
                      type="number"
                    />
                  </Field>

                  <Field label="RunTimeout">
                    <Input
                      className={classes.formInput}
                      placeholder="Optional"
                      value={runTimeout}
                      onChange={(_e, d) => setRunTimeout(d.value)}
                      type="number"
                    />
                  </Field>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className={classes.section}>
        <Subtitle2 className={classes.sectionTitle}>Test settings</Subtitle2>
        <div className={classes.sectionContent}>
          {!selectedType ? (
            <div className={classes.settingsPanel}>
              <Text className={classes.hintText}>
                Select a TestType first to load its machine options and setting defaults.
              </Text>
            </div>
          ) : (
            <>
              {hasGlobalSettingsContent && (
                <div className={classes.settingsPanel}>
                  <Subtitle2 className={classes.panelTitle}>Global settings</Subtitle2>
                  <div className={classes.settingsMachineRow}>
                    {hasConfiguredMachineType && (
                      <Field label="MachineType">
                        <Dropdown
                          className={mergeClasses(classes.formInput, classes.fullWidthDropdownField)}
                          placeholder="Select a machine type"
                          value={effectiveMachineType}
                          selectedOptions={effectiveMachineType ? [effectiveMachineType] : []}
                          onOptionSelect={(_e, data) => {
                            setSelectedMachineType(data.optionValue || '');
                            setSpecifiedMachine('');
                          }}
                        >
                          {machineTypeOptions.map((option) => (
                            <Option key={option} value={option}>{option}</Option>
                          ))}
                        </Dropdown>
                      </Field>
                    )}

                    {requiresMachineType && (
                      <Field label="SpecifiedMachine">
                        {loadingMachines ? (
                          <Spinner size="tiny" />
                        ) : (
                          <Dropdown
                            className={mergeClasses(classes.formInput, classes.fullWidthDropdownField)}
                            value={specifiedMachine || NO_SPECIFIED_MACHINE_LABEL}
                            selectedOptions={[
                              specifiedMachine || NO_SPECIFIED_MACHINE_OPTION,
                            ]}
                            onOptionSelect={(_e, d) => handleSpecifiedMachineChange(d.optionValue)}
                          >
                            <Option value={NO_SPECIFIED_MACHINE_OPTION}>
                              {NO_SPECIFIED_MACHINE_LABEL}
                            </Option>
                            {visibleMachineGroups.map(({ machineType, machines }) => (
                              <OptionGroup key={machineType} label={machineType}>
                                {machines.map((machine) => {
                                  const name = machine.machineName;
                                  return <Option key={`${machineType}:${name}`} value={name}>{name}</Option>;
                                })}
                              </OptionGroup>
                            ))}
                          </Dropdown>
                        )}
                      </Field>
                    )}

                    {globalSettings.map((s) => (
                      <Field key={s.key} label={renderSettingLabel(s, classes.helpIcon)}>
                        {renderSettingInput(s, globalSettingValues[s.key] ?? '', (value) => updateGlobalSetting(s.key, value), defaultValues[s.key])}
                      </Field>
                    ))}
                  </div>
                </div>
              )}

              {selectedTestType?.MachineType !== 'Airflow' && (
                <div className={classes.perTestPanel}>
                  <Subtitle2 className={classes.perTestTitle}>Per-test settings</Subtitle2>
                  <div className={classes.tabBar}>
                    <TabList
                      appearance="filled-circular"
                      selectedValue={activeTab}
                      onTabSelect={(_e, d) => setActiveTab(d.value)}
                    >
                      {tests.map((t) => (
                        <Tab key={t.name} value={t.name}>{t.name}</Tab>
                      ))}
                    </TabList>
                    <Button
                      appearance="subtle"
                      icon={<Add20Regular />}
                      onClick={addTest}
                      disabled={tests.filter((test) => !test.isBase).length >= MAX_EXP_TESTS}
                      title="Add test"
                    />
                    {activeTest && !activeTest.isBase && (
                      <Button
                        appearance="subtle"
                        icon={<Dismiss20Regular />}
                        onClick={() => removeTest(activeTest.name)}
                        title={`Remove ${activeTest.name}`}
                      />
                    )}
                  </div>

                  {activeTest && perTestSettings.length > 0 && (
                    <div className={classes.tabContent}>
                      <div className={classes.testSettingsGrid}>
                        {perTestSettings.map((s) => (
                          <Field key={s.key} label={renderSettingLabel(s, classes.helpIcon)}>
                            {renderSettingInput(
                              s,
                              activeTest.settings[s.key] ?? '',
                              (value) => updateTestSetting(activeTest.name, s.key, value),
                              defaultValues[s.key]
                            )}
                          </Field>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeTest && perTestSettings.length === 0 && globalSettings.length === 0 && (
                    <Text className={classes.hintText}>
                      No settings configured for this test type.
                    </Text>
                  )}
                </div>
              )}

              {selectedTestType?.MachineType === 'Airflow' && activeTest && perTestSettings.length > 0 && (
                <div className={classes.perTestPanel}>
                  {hasGlobalSettingsContent && (
                    <Subtitle2 className={classes.perTestTitle}>Airflow settings</Subtitle2>
                  )}
                  <div className={classes.testSettingsGrid}>
                    {perTestSettings.map((s) => (
                      <Field key={s.key} label={renderSettingLabel(s, classes.helpIcon)}>
                        {renderSettingInput(
                          s,
                          activeTest.settings[s.key] ?? '',
                          (value) => updateTestSetting(activeTest.name, s.key, value),
                          defaultValues[s.key]
                        )}
                      </Field>
                    ))}
                  </div>
                </div>
              )}

              {selectedTestType?.MachineType === 'Airflow' && activeTest && perTestSettings.length === 0 && globalSettings.length === 0 && (
                <div className={classes.perTestPanel}>
                  <Text className={classes.hintText}>
                    No settings configured for this test type.
                  </Text>
                </div>
              )}
            </>
          )}
        </div>
        </section>

      {submitError && (
        <MessageBar intent="error" className={classes.messageBar}>
          <MessageBarBody>{submitError}</MessageBarBody>
        </MessageBar>
      )}
      {submitSuccess && (
        <MessageBar intent="success" className={classes.messageBar}>
          <MessageBarBody>{submitSuccess}</MessageBarBody>
        </MessageBar>
      )}

      <div className={classes.submitRow}>
        <Button
          appearance="primary"
          onClick={handleSubmit}
          disabled={submitting || !selectedType || !jobName.trim()}
        >
          {submitting ? 'Submitting...' : 'Submit'}
        </Button>
      </div>
    </div>
  );
}

export default PolarisSubmitJobPage;
